import * as MuiIcons from '@mui/icons-material';
import type { TableOptions } from '@tanstack/react-table';
import type { DataSchema, NoUnknown, ReactNativeMessagePayloads, Schema } from 'types';
import { AES } from 'crypto-js';

export const createRandomString = (length: number) => {
  const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let result = '';
  for (let i = 0; i < length; i++) {
    result += characters.charAt(Math.floor(Math.random() * characters.length));
  }
  return result;
};

export const encryptApiKey = (apiKey: string, secretKey: string) => {
  const timestamp = Date.now();
  const keyWithTimestamp = `${apiKey}:${timestamp}`;
  const encryptedKey = AES.encrypt(keyWithTimestamp, secretKey).toString();
  return encryptedKey;
};

export const createKey = () => {
  const keyDefault = 'xxx-xxx-xxx';

  return keyDefault.replace(/[x]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

export const throttle = <T extends (...args: unknown[]) => void>(func: T, limit: number = 1): ((...args: Parameters<T>) => void) => {
  let inThrottle: boolean;
  let lastFunc: ReturnType<typeof setTimeout>;
  let lastRan: number;

  return (...args: Parameters<T>): void => {
    if (!inThrottle) {
      func(...args);
      lastRan = Date.now();
      inThrottle = true;
    } else {
      clearTimeout(lastFunc);
      lastFunc = setTimeout(
        () => {
          if (Date.now() - lastRan >= limit) {
            func(...args);
            lastRan = Date.now();
          }
        },
        limit - (Date.now() - lastRan)
      );
    }
  };
};

export const transformDataByDataSchema = <T, K extends DataSchema>(data?: T[], dataSchema?: K): TableOptions<Schema<keyof K, K>>['data'] => {
  if (!data || !dataSchema) {
    return [];
  }

  const result: TableOptions<Schema<keyof K, K>>['data'] = [];

  const schema: { [key: string]: unknown } = {};

  Object.keys(dataSchema).forEach((key) => {
    schema[key] = null;
  });

  data.forEach((item) => {
    const newItem = { ...schema };

    Object.keys(dataSchema).forEach((key) => {
      newItem[key] = item ? (item[key as keyof typeof item] as K[keyof K]['type']) : null;
    });

    newItem['identifier'] = createKey();

    result.push(newItem as Schema<keyof K, K>);
  });

  return result;
};

export const getIconNames = () => {
  const excludedNames = ['sharp', 'twotone', 'rounded', 'outlined'];

  return Object.keys(MuiIcons).filter((iconName: string) => !Array.from(excludedNames).some((name) => iconName.toLowerCase().includes(name)));
};

export const getIcon = (icon: string = 'QuestionMark') => {
  const iconNames = getIconNames();

  const iconData = iconNames.find((iconName) => iconName.toLowerCase() === icon?.toLowerCase());

  const IconComponent = MuiIcons[iconData as keyof typeof MuiIcons];

  return IconComponent;
};

export const isEven = (num: number): boolean => num % 2 === 0;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const areAnyChanges = (prev: any, curr: any): boolean => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const isObject = (value: any) => typeof value === 'object' && value !== null;

  if (isObject(prev) && isObject(curr)) {
    const prevKeys = Object.keys(prev);
    const currKeys = Object.keys(curr);

    if (prevKeys.length !== currKeys.length) {
      return true;
    }

    for (const key of prevKeys) {
      if (!areAnyChanges(prev[key], curr[key])) {
        return false;
      }
    }

    return true;
  }

  return prev !== curr;
};

export const isValueChanged = (oldValue: unknown, newValue: unknown) => JSON.stringify(oldValue) !== JSON.stringify(newValue);

export const normalize = <T extends { id: string }>(input: string | string[] | T | T[]): string[] => {
  if (!input) {
    return [];
  }

  if (typeof input === 'string') {
    return [input];
  }

  if (Array.isArray(input)) {
    return input
      .filter((item): item is string | T => item !== undefined && item !== null)
      .map((item) => (typeof item === 'string' ? item : item.id))
      .toSorted();
  }

  return [];
};

export const isNormalizedValueChanged = <T extends { id: string }>(firstValue: string[] | T[] | T, secondValue: string[] | T[] | T) => {
  const normalizedOld = normalize(firstValue);
  const normalizedNew = normalize(secondValue);

  if (normalizedOld.length !== normalizedNew.length) {
    return true;
  }

  if (normalizedOld.some((value, index) => value !== normalizedNew[index])) {
    return true;
  }

  return false;
};

export const normalizeString = (str: string): string =>
  str
    .normalize('NFC')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();

export const decodeURI = (str: string): string => decodeURIComponent(str);

export const shortenText = (text: string, maxLength: number): string => {
  if (text.length <= maxLength) {
    return text;
  }

  return `${text.slice(0, maxLength)}...`;
};

export const paginateArray = <T>(items: T[] = [], page: number = 1, pageSize: number = 10): T[] => {
  const startIndex = Math.max(page - 1, 0) * pageSize;
  return items.slice(startIndex, startIndex + pageSize);
};

export const getObjectValue = (item?: Record<string, string> | string | null) => {
  if (!item) return null;

  if (typeof item === 'object' && item !== null) {
    return 'value' in item ? item.value : null;
  }

  return item;
};

export const isMobileApp = () => !!(window as Window & { isMobileApp?: boolean }).isMobileApp;

export const invokeRNMessage = <T extends keyof ReactNativeMessagePayloads, P extends ReactNativeMessagePayloads[T]>(
  type: T,
  payload: NoUnknown<ReactNativeMessagePayloads[T], P>
): void => {
  if (isMobileApp()) {
    (window as Window & { ReactNativeWebView?: { postMessage: (message: string) => void } }).ReactNativeWebView?.postMessage(
      JSON.stringify({
        type,
        payload,
      })
    );
  }
};

export const isStringArray = (arr: unknown[]): arr is string[] => {
  return arr.length === 0 || typeof arr[0] === 'string';
};

export const normalizeToArray = <T = string>(val: unknown): T[] => {
  if (val === undefined) return [];
  return Array.isArray(val) ? val : [val as T];
};

export const getUUID = () => {
  return crypto.randomUUID();
};

export const safelyStringifyToJSON = (obj: unknown): string | undefined => {
  try {
    const seen = new WeakSet();
    return JSON.stringify(
      obj,
      (_key, value) => {
        if (typeof value === 'object' && value !== null) {
          if (seen.has(value)) {
            return '[Circular reference]';
          }
          seen.add(value);
        }
        if (typeof value === 'bigint') {
          return value.toString();
        }
        return value;
      },
      2
    );
  } catch (error) {
    return undefined;
  }
};
